import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { PICKUP_TYPE_DOCTOR, PICKUP_TYPE_HOME } from '../constants';
import { getTransportLabelSx } from './transportTypographySx';
import InlineLocationForm from './InlineLocationForm';

Location.propTypes = {
  doctorsList: PropTypes.array,
  isLoading: PropTypes.bool,
  formState: PropTypes.object.isRequired,
  setFields: PropTypes.func.isRequired,
  labelText: PropTypes.string.isRequired,
  locationTypeField: PropTypes.string.isRequired,
  doctorIdField: PropTypes.string.isRequired,
  disableToggle: PropTypes.bool,
  toggleTooltip: PropTypes.string,
  disableSelection: PropTypes.bool,
  detailsMode: PropTypes.bool,
  refetchDoctors: PropTypes.func,
};

export default function Location({
  doctorsList,
  isLoading,
  formState,
  setFields,
  labelText,
  locationTypeField,
  doctorIdField,
  disableToggle = false,
  toggleTooltip = '',
  disableSelection = false,
  detailsMode = false,
  refetchDoctors,
}) {
  const [pendingDoctorId, setPendingDoctorId] = useState(null);
  const [isAddingLocation, setIsAddingLocation] = useState(false);

  const locationTypes = [
    { label: 'HOME', value: PICKUP_TYPE_HOME },
    { label: "DOCTOR'S OFFICE", value: PICKUP_TYPE_DOCTOR },
  ];

  // When loading completes and we have a pending doctor ID, select it
  useEffect(() => {
    if (!isLoading && pendingDoctorId) {
      setFields(doctorIdField, pendingDoctorId);
      setPendingDoctorId(null);
    }
  }, [isLoading, pendingDoctorId, doctorIdField, setFields]);

  useEffect(() => {
    if (disableSelection) {
      setIsAddingLocation(false);
    }
  }, [disableSelection]);

  const autocompleteDisabled = !formState.FacilityId || disableSelection;
  const rowControlHeight = 35;
  const rowButtonSx = { height: rowControlHeight };
  const autocompleteSx = {
    minWidth: 350,
    '& .MuiOutlinedInput-root': { height: rowControlHeight },
    '& .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: '0 4px' },
  };

  return (
    <Grid container spacing={1} pl={1} alignItems="center">
      <Grid item>
        <Typography color="text.secondary" sx={getTransportLabelSx(detailsMode)}>
          {labelText}
        </Typography>
      </Grid>
      <Grid item>
        {/* Span wrapper lets the tooltip show on hover even though the disabled buttons ignore pointer events */}
        <Tooltip title={toggleTooltip} placement="top" arrow>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <ToggleButtonGroup
              value={formState[locationTypeField]}
              exclusive
              onChange={(_, val) => {
                if (val && !disableToggle) {
                  setFields(locationTypeField, val);
                }
              }}
              size="small"
              disabled={disableToggle}
              sx={{ '& .MuiToggleButton-root': rowButtonSx }}
            >
              {locationTypes.map((type) => (
                <ToggleButton key={type.value} value={type.value} disabled={disableToggle}>
                  {type.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Tooltip>
      </Grid>
      {formState[locationTypeField] === PICKUP_TYPE_DOCTOR && !disableSelection && (
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => setIsAddingLocation(true)}
            disabled={!formState.FacilityId}
            sx={rowButtonSx}
          >
            + Add Location
          </Button>
        </Grid>
      )}
      {/* Doctor Dropdown (shown only if DOCTOR'S OFFICE is selected) */}
      {formState[locationTypeField] === PICKUP_TYPE_DOCTOR && (
        <Grid item>
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            disabled={autocompleteDisabled}
            value={doctorsList.find((d) => d.value === formState[doctorIdField]) ?? null}
            onChange={(_, newValue) => setFields(doctorIdField, newValue?.value ?? '')}
            options={isLoading ? [] : doctorsList}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={autocompleteDisabled ? '' : 'Select a doctor'}
                autoComplete="off"
              />
            )}
            loading={isLoading}
          />
        </Grid>
      )}
      {formState[locationTypeField] === PICKUP_TYPE_DOCTOR && isAddingLocation && (
        <Box sx={{ width: '100%' }}>
          <InlineLocationForm
            disabled={!formState.FacilityId}
            facilityId={formState.FacilityId}
            onAdded={(newDoctorId) => {
              setIsAddingLocation(false);
              if (newDoctorId && refetchDoctors) {
                // Trigger refetch and store the pending doctor ID
                setPendingDoctorId(newDoctorId);
                refetchDoctors();
              }
            }}
          />
        </Box>
      )}
    </Grid>
  );
}
